import type { PrioritizationStaticContent } from '@core-api/presentation/types/response';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { getVisualizationState } from '../../utils/getVisualizationState';
import { useScreenshotReady } from '../../shared/hooks/use-screenshot-ready';
import { HundredPointsPresentation } from './HundredPointsPresentation';
import { useHundredPointsPreviewResults } from './hooks/use-hundred-points-preview-results';
import { useHundredPointsResults } from './hooks/use-hundred-points-results';

export function HundredPointsPresentationDataHandler() {
  const {
    InteractiveCanvasComponents,
    useConfig,
    useCurrentSession,
    usePace,
    isPreviewResultsEnabled,
  } = usePresentationContext();
  const { enableAnimations, mode } = useConfig();
  const { currentStep } = usePace();
  const slide = usePresentationSlideContext();
  const { isCurrentSessionActive, activeSessionId } = useCurrentSession();
  const staticContent = slide.staticContent as PrioritizationStaticContent;
  const interactiveContent = slide.interactiveContents[0]!;

  const visualizationState = getVisualizationState({
    mode,
    currentStep,
  });

  const shouldHideResults =
    slide.responseVisibility === 'hidden' ||
    visualizationState === 'hide-answers';

  const { results, isLoading } = useHundredPointsResults({
    adminKey: slide.slideAdminKey,
    interactiveContentId: interactiveContent.interactiveContentId,
    shouldFetchResults: !shouldHideResults && !isPreviewResultsEnabled,
    sessionId: isCurrentSessionActive ? undefined : activeSessionId,
  });

  const previewResults = useHundredPointsPreviewResults({
    isEnabled: isPreviewResultsEnabled,
    choices: interactiveContent.choices,
  });

  useScreenshotReady({ isReady: mode === 'screenshot' && !isLoading });

  return (
    <HundredPointsPresentation
      title={slide.title}
      styledTitle={staticContent.styledTitle as Record<string, unknown>}
      label={staticContent.label}
      longerDescription={slide.interactiveContents[0]!.description}
      fontSizeOffset={staticContent.textSizeOffset}
      shouldHideResults={shouldHideResults}
      choices={interactiveContent.choices.map((choice) => ({
        id: choice.interactiveContentChoiceId,
        label: choice.title,
      }))}
      shouldAnimate={enableAnimations}
      results={isPreviewResultsEnabled ? previewResults : results}
      CanvasVisualizationArea={
        InteractiveCanvasComponents?.CanvasVisualizationArea
      }
    />
  );
}
